import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Delete,
  History,
  Grid3x3,
  Loader2,
  MessageSquare,
  Mic,
  MicOff,
  Pause,
  Phone,
  PhoneIncoming,
  PhoneOff,
  Play,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import voiceService from '@/services/voice.service';
import useVoiceStore from '@/stores/voiceStore';
import useAuthStore from '@/stores/authStore';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

interface CallStatusProps {
  /** Schmale Seitenleiste — dann nur das Symbol. */
  collapsed?: boolean;
}

/**
 * Telefonkarte in der Seitenleiste.
 *
 * Immer sichtbar — auch wenn die Telefonie noch nicht eingerichtet ist. Eine
 * Karte, die je nach Zustand verschwindet, wirkt wie ein Fehler; stattdessen
 * zeigt sie den Zustand an und führt Admins in die Einstellungen.
 *
 * Enthält Zustand, Wählfeld und die Gesprächssteuerung, damit während eines
 * Anrufs alles an einem Ort liegt.
 */
export default function CallStatus({ collapsed = false }: CallStatusProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const {
    call, muted, held, connection, error, recording, lines, activeLineOrgId,
    connect, reconnect, setActiveLine, dial, hangup, answer, toggleMute, toggleHold, sendDtmf, toggleRecording,
  } = useVoiceStore();

  const [open, setOpen] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [showKeypad, setShowKeypad] = useState(false);
  const [number, setNumber] = useState('');
  const [dialing, setDialing] = useState(false);

  // Leitungen statt Einzel-Status: ein Agent kann in mehreren Organisationen
  // Mitglied sein — Telefonie gilt als eingerichtet, sobald IRGENDEINE davon
  // eine aktive Nummer hat.
  const { data: lineList } = useQuery({
    queryKey: ['voice-lines'],
    queryFn: () => voiceService.getLines(),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const enabled = Boolean(lineList?.length);

  // Warteschlange — nur pollen, wenn Telefonie überhaupt aktiv ist.
  const { data: queue } = useQuery({
    queryKey: ['voice-queue'],
    queryFn: () => voiceService.getQueue(),
    enabled,
    refetchInterval: 8_000,
    retry: false,
  });
  const queueCount = queue?.count ?? 0;

  // SMS (nur Empfang): Leitungen, deren Nummer SMS empfängt. Angezeigt wird die
  // aktive Leitung; Zähler ungelesener SMS läuft über alle Leitungen.
  const queryClient = useQueryClient();
  const smsLines = (lineList || []).filter((l) => l.sms_enabled);
  const smsOn = smsLines.length > 0;
  const smsOrgId = smsLines.some((l) => l.organization_id === activeLineOrgId)
    ? activeLineOrgId
    : smsLines[0]?.organization_id;
  const { data: smsUnread } = useQuery({
    queryKey: ['voice-sms-unread'],
    queryFn: () => voiceService.getSmsUnread(),
    enabled: smsOn,
    refetchInterval: 30_000,
    retry: false,
  });
  const smsUnreadTotal = Object.values(smsUnread || {}).reduce((a, b) => a + b, 0);
  const { data: smsList } = useQuery({
    queryKey: ['voice-sms', smsOrgId],
    queryFn: () => voiceService.getSms(smsOrgId || undefined, 20),
    enabled: smsOn && open && Boolean(smsOrgId),
    refetchInterval: open ? 30_000 : false,
    retry: false,
  });
  const [expandedSms, setExpandedSms] = useState<string | null>(null);
  const refreshSms = () => {
    void queryClient.invalidateQueries({ queryKey: ['voice-sms'] });
    void queryClient.invalidateQueries({ queryKey: ['voice-sms-unread'] });
  };
  const markSmsRead = async (ids: string[]) => {
    try { await voiceService.markSmsRead({ ids }); refreshSms(); } catch { /* still anzeigen */ }
  };
  const markAllSmsRead = async () => {
    try { await voiceService.markSmsRead({ organizationId: smsOrgId || undefined }); refreshSms(); } catch { /* still anzeigen */ }
  };
  const smsWhen = (iso: string) => {
    const min = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
    if (min < 1) return t('shell:voice.queueJustNow', 'gerade eben');
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h} h`;
    return new Date(iso).toLocaleDateString();
  };

  const pullNext = async () => {
    try {
      await voiceService.pullNextFromQueue(queue?.items?.[0]?.organization_id);
      // Der Anruf klingelt gleich als normaler eingehender Anruf.
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('shell:voice.queuePullFailed', 'Annehmen fehlgeschlagen'));
    }
  };

  const waitingSince = (iso: string) => {
    const min = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
    return min < 1 ? t('shell:voice.queueJustNow', 'gerade eben') : `${min} min`;
  };

  // Registrieren, sobald Telefonie freigeschaltet ist — sonst könnte kein
  // eingehender Anruf zugestellt werden. Mikrofonzugriff wird dabei NICHT
  // angefragt, das passiert erst beim tatsächlichen Gespräch.
  useEffect(() => {
    if (enabled && connection === 'idle') void connect();
  }, [enabled, connection, connect]);

  // Leitungen sind NACHTRÄGLICH aufgetaucht (Nummer gekauft/zugewiesen,
  // Query neu geladen), aber der Store hält noch null Registrierungen —
  // dann neu verbinden. Ohne das würde kein eingehender Anruf klingeln.
  // Ein Versuch je Query-Stand: widersprechen sich Query und Server dauerhaft,
  // darf daraus keine Endlosschleife gegen /voice/lines werden.
  const reconnectTriedFor = useRef<unknown>(null);
  useEffect(() => {
    if (!enabled || connection !== 'ready' || lines.length > 0 || call) return;
    if (reconnectTriedFor.current === lineList) return;
    reconnectTriedFor.current = lineList;
    void reconnect();
  }, [enabled, connection, lines.length, call, lineList, reconnect]);

  useEffect(() => {
    if (!call?.answeredAt) { setElapsed(0); return; }
    const tick = () => setElapsed(Math.floor((Date.now() - call.answeredAt!) / 1000));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [call?.answeredAt]);

  // Ein eingehender Anruf soll ohne Zutun bedienbar sein.
  useEffect(() => {
    if (call?.direction === 'inbound' && call.phase === 'ringing') setOpen(true);
    if (!call) setShowKeypad(false);
  }, [call?.direction, call?.phase, call]);

  const isIncomingRinging = call?.direction === 'inbound' && call.phase === 'ringing';

  const label = call
    ? (call.displayName || call.number || t('shell:voice.unknownNumber', 'Unbekannt'))
    : !enabled
      ? t('shell:voice.notConfigured', 'Telefon nicht eingerichtet')
      : connection === 'ready'
        ? t('shell:voice.ready', 'Telefon bereit')
        : connection === 'connecting'
          ? t('shell:voice.connecting', 'Verbindet …')
          : connection === 'error'
            ? t('shell:voice.offline', 'Telefon offline')
            : t('shell:voice.idle', 'Telefon inaktiv');

  const detail = !call
    ? null
    : isIncomingRinging
      ? t('shell:voice.incoming', 'Eingehender Anruf')
      : call.phase === 'dialing'
        ? t('shell:voice.dialing', 'Wird gewählt …')
        : call.phase === 'ringing'
          ? t('shell:voice.ringing', 'Es klingelt …')
          : call.phase === 'ending'
            ? t('shell:voice.ending', 'Wird beendet …')
            : held
              ? t('shell:voice.onHold', 'Gehalten')
              : formatDuration(elapsed);

  const dotColor = !enabled
    ? 'bg-slate-400'
    : connection === 'ready'
      ? 'bg-emerald-500'
      : connection === 'connecting'
        ? 'bg-amber-500'
        : connection === 'error'
          ? 'bg-red-500'
          : 'bg-slate-400';

  const submitDial = async () => {
    const trimmed = number.trim();
    if (!trimmed) return;
    setDialing(true);
    try {
      await dial({ number: trimmed });
      setNumber('');
    } catch (e: any) {
      toast.error(e?.message || t('shell:voice.dialFailed', 'Anruf konnte nicht gestartet werden'));
    } finally {
      setDialing(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-live={call ? 'true' : 'false'}
          className={cn(
            'vyndesk-call-status flex items-center gap-2 rounded-md border text-xs font-medium transition',
            collapsed ? 'h-8 w-8 justify-center px-0' : 'h-9 w-full px-2.5',
          )}
          title={`${t('shell:voice.statusTitle', 'Telefonstatus')} — ${label}${detail ? ` · ${detail}` : ''}`}
        >
          {call?.direction === 'inbound' ? (
            <PhoneIncoming className={cn('h-3.5 w-3.5 shrink-0', isIncomingRinging && 'animate-pulse')} />
          ) : (
            <Phone className={cn('h-3.5 w-3.5 shrink-0', call && call.phase !== 'active' && 'animate-pulse')} />
          )}

          {!collapsed && (
            <>
              <span className="min-w-0 flex-1 truncate text-left">{label}</span>
              {/* Wartende Anrufer: dezenter Zähler, kein zweites UI-Element */}
              {queueCount > 0 && !call && (
                <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-amber-700">
                  {queueCount}
                </span>
              )}
              {/* Ungelesene SMS — gleiche Bauart wie der Warteschlangen-Zähler */}
              {smsUnreadTotal > 0 && !call && (
                <span
                  className="flex shrink-0 items-center gap-0.5 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-sky-700"
                  title={t('shell:voice.smsTitle', 'SMS')}
                >
                  <MessageSquare className="h-2.5 w-2.5" />
                  {smsUnreadTotal}
                </span>
              )}
              {detail
                ? <span className="shrink-0 tabular-nums opacity-70">{detail}</span>
                : <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', dotColor)} />}
            </>
          )}
          {collapsed && queueCount > 0 && !call && (
            <span className="absolute -mt-4 ml-4 rounded bg-amber-500 px-1 text-[9px] font-bold text-white">
              {queueCount}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent side="right" align="end" className="w-64 rounded-md p-3">
        {/* 1) Noch nicht eingerichtet */}
        {!enabled && !call && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">
              {t('shell:voice.notConfigured', 'Telefon nicht eingerichtet')}
            </p>
            <p className="text-xs text-muted-foreground">
              {user?.role === 'admin'
                ? t('shell:voice.setupHintAdmin', 'Rufnummer in den Einstellungen auswählen, dann kann telefoniert werden.')
                : t('shell:voice.setupHintAgent', 'Ein Administrator muss zuerst eine Rufnummer einrichten.')}
            </p>
            {user?.role === 'admin' && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 w-full rounded-md"
                onClick={() => { setOpen(false); navigate('/settings/voice'); }}
              >
                {t('shell:voice.openSettings', 'Einstellungen öffnen')}
              </Button>
            )}
          </div>
        )}

        {/* 2) Eingerichtet, kein Gespräch → Warteschlange + Wählfeld */}
        {enabled && !call && (
          <>
            {queueCount > 0 && (
              <div className="mb-3 rounded-md border border-amber-200 bg-amber-50/70 p-2.5">
                <div className="mb-1.5 flex items-center justify-between">
                  <p className="text-xs font-semibold text-amber-800">
                    {t('shell:voice.queueTitle', 'Warteschlange')} · {queueCount}
                  </p>
                  <Button
                    size="sm"
                    className="h-6 rounded bg-amber-600 px-2 text-[11px] text-white hover:bg-amber-700"
                    onClick={() => void pullNext()}
                  >
                    {t('shell:voice.queueAnswer', 'Annehmen')}
                  </Button>
                </div>
                <ul className="space-y-0.5">
                  {(queue?.items || []).slice(0, 5).map((q) => (
                    <li key={q.id} className="flex items-center justify-between gap-2 text-[11px] text-amber-900/80">
                      <span className="min-w-0 truncate tabular-nums">
                        {q.from_number || t('shell:voice.unknownNumber', 'Unbekannt')}
                        {lines.length > 1 && q.organization_name ? ` · ${q.organization_name}` : ''}
                      </span>
                      <span className="shrink-0 opacity-70">{waitingSince(q.started_at)}</span>
                    </li>
                  ))}
                  {queueCount > 5 && (
                    <li className="text-[11px] text-amber-700/70">+{queueCount - 5}</li>
                  )}
                </ul>
              </div>
            )}

            {lines.length > 1 && (
              <label className="mb-1.5 block">
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('shell:voice.line', 'Leitung')}
                </span>
                <select
                  value={activeLineOrgId || ''}
                  onChange={(e) => setActiveLine(e.target.value)}
                  className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  {lines.map((l) => (
                    <option key={l.organization_id} value={l.organization_id}>
                      {l.organization_name} · {l.number}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <div className="flex items-center gap-1.5">
              <Input
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void submitDial(); }}
                placeholder="+49…"
                className="h-9 rounded-md text-sm tabular-nums"
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0 rounded-md text-muted-foreground"
                onClick={() => setNumber((v) => v.slice(0, -1))}
                disabled={!number}
                title={t('shell:voice.backspace', 'Löschen')}
              >
                <Delete className="h-4 w-4" />
              </Button>
            </div>

            <div className="mt-2.5 grid grid-cols-3 gap-1.5">
              {KEYS.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setNumber((v) => v + k)}
                  className="rounded-md border border-border py-2 text-sm font-medium tabular-nums text-foreground transition hover:bg-accent hover:text-accent-foreground"
                >
                  {k}
                </button>
              ))}
            </div>

            <Button
              className="mt-2.5 h-9 w-full rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
              onClick={submitDial}
              disabled={dialing || !number.trim()}
            >
              {dialing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Phone className="mr-2 h-4 w-4" />}
              {t('shell:voice.dialer', 'Anrufen')}
            </Button>

            {/* Fehler des letzten Versuchs stehen lassen, bis neu gewählt wird —
                sonst verschwindet der einzige Hinweis, warum nichts passiert ist. */}
            {error ? (
              <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] leading-4 text-red-700">
                {error}
              </p>
            ) : (
              <p className="mt-2 text-[11px] leading-4 text-muted-foreground">
                {t('shell:voice.dialerHint', 'Nummer im Format +49…')}
              </p>
            )}
          </>
        )}

        {/* 3) Laufendes Gespräch → Steuerung */}
        {call && (
          <>
            <p className="truncate text-sm font-semibold text-foreground">{label}</p>
            {/* Über welche Leitung läuft der Anruf — bei mehreren Shops die
                entscheidende Information VOR dem Abnehmen. */}
            {call.lineLabel && (lines.length > 1 || call.direction === 'inbound') && (
              <p className="mt-0.5 truncate text-[11px] font-medium text-primary/80">{call.lineLabel}</p>
            )}
            <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">{detail}</p>

            {showKeypad && call.phase === 'active' && (
              <div className="mt-2.5 grid grid-cols-3 gap-1.5">
                {KEYS.map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => sendDtmf(k)}
                    className="rounded-md border border-border py-1.5 text-sm font-medium tabular-nums text-foreground transition hover:bg-accent hover:text-accent-foreground"
                  >
                    {k}
                  </button>
                ))}
              </div>
            )}

            <div className="mt-3 flex items-center gap-1.5">
              {isIncomingRinging ? (
                <>
                  <Button size="sm" className="h-8 flex-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700" onClick={answer}>
                    <Phone className="mr-1.5 h-3.5 w-3.5" />
                    {t('shell:voice.answer', 'Annehmen')}
                  </Button>
                  <Button size="sm" variant="destructive" className="h-8 flex-1 rounded-md" onClick={hangup}>
                    {t('shell:voice.decline', 'Ablehnen')}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="icon"
                    variant={muted ? 'default' : 'outline'}
                    className="h-8 w-8 rounded-md"
                    onClick={toggleMute}
                    disabled={call.phase !== 'active'}
                    title={muted ? t('shell:voice.unmute', 'Stummschaltung aufheben') : t('shell:voice.mute', 'Stummschalten')}
                  >
                    {muted ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
                  </Button>
                  <Button
                    size="icon"
                    variant={held ? 'default' : 'outline'}
                    className="h-8 w-8 rounded-md"
                    onClick={toggleHold}
                    disabled={call.phase !== 'active'}
                    title={held ? t('shell:voice.resume', 'Fortsetzen') : t('shell:voice.hold', 'Halten')}
                  >
                    {held ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
                  </Button>
                  {/* Aufzeichnung: nur eingehende Anrufe (ausgehende haben kein
                      Call-Control-Leg). Agent holt vorher die mündliche
                      Einwilligung ein — der Klick protokolliert sie. */}
                  {call.direction === 'inbound' && (
                    <Button
                      size="icon"
                      variant="outline"
                      className={cn('h-8 w-8 rounded-md', recording && 'border-red-300 bg-red-50 text-red-600 hover:bg-red-100')}
                      onClick={() => {
                        void toggleRecording().catch(() =>
                          toast.error(t('shell:voice.recordFailed', 'Aufzeichnung konnte nicht gestartet werden')),
                        );
                      }}
                      disabled={call.phase !== 'active'}
                      title={recording
                        ? t('shell:voice.recordStop', 'Aufzeichnung beenden')
                        : t('shell:voice.record', 'Aufzeichnen (Einwilligung einholen!)')}
                    >
                      <span className={cn('h-2.5 w-2.5 rounded-full', recording ? 'animate-pulse bg-red-600' : 'bg-red-500/70')} />
                    </Button>
                  )}
                  <Button
                    size="icon"
                    variant={showKeypad ? 'default' : 'outline'}
                    className="h-8 w-8 rounded-md"
                    onClick={() => setShowKeypad((v) => !v)}
                    disabled={call.phase !== 'active'}
                    title={t('shell:voice.keypad', 'Tastenfeld')}
                  >
                    <Grid3x3 className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="destructive" className="ml-auto h-8 rounded-md" onClick={hangup}>
                    <PhoneOff className="mr-1.5 h-3.5 w-3.5" />
                    {t('shell:voice.hangup', 'Auflegen')}
                  </Button>
                </>
              )}
            </div>
          </>
        )}

        {/* Eingehende SMS — nur lesen. Antworten gibt es bewusst nicht. */}
        {smsOn && !call && (
          <div className="mt-3 border-t border-border pt-2.5">
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                <MessageSquare className="h-3.5 w-3.5" />
                {t('shell:voice.smsTitle', 'SMS')}
                {smsUnreadTotal > 0 && (
                  <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-sky-700">
                    {smsUnreadTotal}
                  </span>
                )}
              </p>
              {(smsList || []).some((m) => !m.read_at) && (
                <button
                  type="button"
                  onClick={() => void markAllSmsRead()}
                  className="text-[11px] font-medium text-muted-foreground hover:text-foreground"
                >
                  {t('shell:voice.smsMarkAllRead', 'Alle gelesen')}
                </button>
              )}
            </div>
            {smsLines.length > 1 && (
              <p className="mb-1 text-[10px] text-muted-foreground">
                {smsLines.find((l) => l.organization_id === smsOrgId)?.organization_name}
              </p>
            )}
            {!smsList?.length ? (
              <p className="text-[11px] text-muted-foreground">{t('shell:voice.smsEmpty', 'Noch keine SMS')}</p>
            ) : (
              <ul className="max-h-56 space-y-1 overflow-y-auto pr-0.5">
                {smsList.map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setExpandedSms(expandedSms === m.id ? null : m.id);
                        if (!m.read_at) void markSmsRead([m.id]);
                      }}
                      className="w-full rounded-md border border-border px-2 py-1.5 text-left hover:bg-muted/50"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className={cn(
                          'min-w-0 truncate text-[11px] tabular-nums',
                          m.read_at ? 'text-muted-foreground' : 'font-semibold text-foreground',
                        )}>
                          {m.from_number}
                        </span>
                        <span className="shrink-0 text-[10px] text-muted-foreground">{smsWhen(m.received_at)}</span>
                      </div>
                      <p
                        className="mt-0.5 whitespace-pre-wrap break-words text-[11px] text-foreground/90"
                        style={expandedSms === m.id
                          ? undefined
                          : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                      >
                        {m.body}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Anrufliste — der Menüpunkt „Anrufe" lebt jetzt hier */}
        <button
          type="button"
          onClick={() => { setOpen(false); navigate('/anrufe'); }}
          className="mt-2 flex w-full items-center justify-center gap-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground"
        >
          <History className="h-3 w-3" />
          {t('shell:calls.title', 'Anrufe')}
        </button>
      </PopoverContent>
    </Popover>
  );
}
